import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { DASHBOARD_LAYOUTS, DashboardLayoutKey } from '../layouts';

interface DashboardDialogProps {
  open: boolean;
  mode: 'create' | 'rename';
  initialName?: string;
  onCancel: () => void;
  onCreate: (name: string, layoutKey: DashboardLayoutKey) => void;
  onRename: (name: string) => void;
}

const DashboardDialog: React.FC<DashboardDialogProps> = ({
  open,
  mode,
  initialName = '',
  onCancel,
  onCreate,
  onRename,
}) => {
  const [name, setName] = useState(initialName);
  const [layoutKey, setLayoutKey] = useState<DashboardLayoutKey>(DASHBOARD_LAYOUTS[0].key);

  useEffect(() => {
    if (open) {
      setName(initialName);
    }
  }, [open, initialName]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (mode === 'create') {
      onCreate(trimmed, layoutKey);
    } else {
      onRename(trimmed);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {mode === 'create' ? 'New Dashboard' : 'Rename Dashboard'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '16px !important' }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          autoFocus
        />

        {mode === 'create' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Layout</InputLabel>
            <Select
              label="Layout"
              value={layoutKey}
              onChange={(e) => setLayoutKey(e.target.value as DashboardLayoutKey)}
            >
              {DASHBOARD_LAYOUTS.map((layout) => (
                <MenuItem key={layout.key} value={layout.key}>
                  {layout.label} — {layout.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!name.trim()}>
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardDialog;
